<script setup lang="ts">
/**
 * AppToast — 전역 토스트 단일 표시기.
 *
 * App.vue 에 1개만 마운트되며, notification 스토어의 토스트 목록을 화면 우상단에 렌더한다.
 * 에러 토스트의 출처는 http.ts 인터셉터(pushError) 단일 — 컴포넌트가 직접 띄우지 않는다(중복 알림 차단).
 * 성공 토스트(pushSuccess)는 동작 결과 확인용으로 컴포넌트가 직접 호출한다. 색은 톤별 토큰 경유
 * (error=danger, success=ok), 닫기·자동 소멸은 스토어가 관리.
 */
import { X } from 'lucide-vue-next';

import type { ToastTone } from '#/stores/notification';
import { useNotificationStore } from '#/stores/notification';

const notification = useNotificationStore();

/** 톤 → 카드 보더·점 색 클래스(토큰 경유). */
const TONE_CLASS: Record<ToastTone, { border: string; dot: string }> = {
  error: { border: 'border-danger', dot: 'bg-danger' },
  success: { border: 'border-ok', dot: 'bg-ok' },
};
</script>

<template>
  <!-- aria-live: 스크린리더가 새 에러를 읽도록(공손한 알림). 포인터 통과 + 카드만 활성. -->
  <!--
    위치: sticky 헤더(z-header·top-0·높이 header=74px) 직하단에 고정한다.
    - top: 헤더 높이(header 74px) + 여백 — 페이지 최상단에서 헤더/GNB 에 가려져 토스트가 잘리던 문제 해결.
    - z-toast(100): 헤더(z-header 80)·모달(z-modal 90) 보다 위로 올려 항상 최상단에 뜨게 한다.
  -->
  <div
    class="pointer-events-none fixed inset-x-0 top-[calc(theme(spacing.header)+12px)] z-toast flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end sm:px-0"
    role="status"
    aria-live="polite"
  >
    <div
      v-for="toast in notification.toasts"
      :key="toast.id"
      class="pointer-events-auto flex w-full max-w-[420px] animate-fo-fade items-start gap-3 rounded-[12px] border bg-white px-4 py-3 shadow-md"
      :class="TONE_CLASS[toast.tone].border"
    >
      <span
        class="mt-px h-2 w-2 flex-none rounded-full"
        :class="TONE_CLASS[toast.tone].dot"
        aria-hidden="true"
      />
      <p class="min-w-0 flex-1 text-[13.5px] leading-[1.55] text-ink">{{ toast.message }}</p>
      <button
        type="button"
        class="-mr-1 -mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md text-muted transition-colors hover:bg-bg-2 hover:text-ink"
        aria-label="알림 닫기"
        @click="notification.dismiss(toast.id)"
      >
        <X class="h-4 w-4" :stroke-width="2" />
      </button>
    </div>
  </div>
</template>
